import { FieldArray } from 'react-final-form-arrays';
import { connect } from 'react-redux';
import { useCallback, useEffect, useMemo } from 'react';
import { Field, FormSpy } from 'react-final-form';
import { groupBy } from 'ramda';

import { ShowMoreButton } from 'UIPackage/MainModule/components/ShowMoreButton/ShowMoreButton';
import { Spinner } from 'UIPackage/MainModule/components/Spinner';
import { IKnmErknmFormAct } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormAct';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { booleanSelectOptions } from 'KnmPackage/MainModule/constants/booleanSelectOptions';
import { Button } from 'UIPackage/MainModule/components/Button';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { Row } from 'UIPackage/FormModule/components/Row';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { getCatalogsKnmActTypesSelectOptions } from 'CorePackage/CatalogsModule/selectors/catalogsSelectors';
import { IKnmActTypeSelectOption } from 'CorePackage/CatalogsModule/interfaces/IKnmActTypeSelectOption';
import { IRequirementsNpaInitValues } from 'KnmPackage/RequirementsNpaModalModule/components/RequirementsNpaModal';
import { IBooleanSelectOption } from 'KnmPackage/MainModule/interfaces/IBooleanSelectOption';
import { ViolationStatusesEnum } from 'APIPackage/KnmModule/enums/ViolationStatusesEnum';
import { IKnmForm } from 'KnmPackage/MainModule/interfaces/IKnmForm';
import { getFieldArrayErrorMessage } from 'CorePackage/MainModule/common/getFieldArrayErrorMessage';
import { IKnmErknmFormViolatedRequirementsNpa } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormViolatedRequirementsNpa';
import { KnmViolatedRequirementsNpaItem } from 'KnmPackage/KnmViolatedRequirementsNpa/components/KnmViolatedRequirementsNpaItem';
import { IDecisionActForm } from 'DecisionPackage/MainModule/interfaces/IDecisionActForm';
import { IDecisionFormRef } from 'DecisionPackage/MainModule/interfaces/IDecisionFormRef';
import { getTitleFieldWithRedStar } from 'functions/getTitleFieldWithRedStar';
import { GroupedViolatedRequirementsNpaItem } from 'KnmPackage/KnmViolatedRequirementsNpa/components/GroupedViolatedRequirementsNpaItem';
import { IKnmErknmFormRequirementNpa } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormRequirementNpa';
import { getViolationsByReasonLink } from 'DecisionPackage/DecisionWillBeAct/functions/getViolationsByReasonLink';
import { convertViolationDataToKnmErknmFormViolatedRequirementsNpa } from 'DecisionPackage/DecisionWillBeAct/functions/convertViolationDataToKnmErknmFormViolatedRequirementsNpa';
import { IDecisionFormReason } from 'DecisionPackage/MainModule/interfaces/IDecisionFormReason';

import styles from './ErknmActViolations.module.scss';
import { useShowMore } from '../../../../../../hooks/useShowMore';
import {
  getKnmErknmIsPrescriptionFailed,
  getKnmErknmIsPrescriptionOldRules,
} from '../../../../../MainModule/selectors/knmErknmSelectors';
import {
  getDecisionIsPrescriptionFailed,
  getDecisionIsPrescriptionOldRules,
} from '../../../../../../DecisionPackage/MainModule/selectors/decisionSelectors';

interface IErknmActViolationsProps {
  addRequirementsNpa: (actFieldName: string, fieldName: string) => void;
  addViolatedMandatory: (initialValue: IRequirementsNpaInitValues, fieldName: string, parentFieldName?: string) => void;
  id: string;
  index?: number;
  isWatchOnly: boolean;
  name: string;
  value: IKnmErknmFormAct | IDecisionActForm;
  isDisabledField: boolean;
  isDisabledByDirty?: boolean;
  isDecision?: boolean;
  isPaperless?: boolean;
  formRef: IKnmFormRef | IDecisionFormRef | undefined;
  isEnablePrescriptionCheck: boolean;
  isPrescriptionCheck: boolean;
}

interface IErknmActViolationsStoreProps {
  knmActTypesOptions: IKnmActTypeSelectOption[] | undefined;
  isPassportByCheckPrescriptionFailed: boolean | undefined;
  isPassportByCheckPrescriptionOldRules: boolean | undefined;
}

const ErknmActViolationsWithoutStore = ({
  addRequirementsNpa,
  addViolatedMandatory,
  id,
  index,
  isWatchOnly,
  name,
  value,
  formRef,
  knmActTypesOptions,
  isDisabledField,
  isDecision,
  isPaperless,
  isDisabledByDirty = false,
  isEnablePrescriptionCheck,
  isPrescriptionCheck,
  isPassportByCheckPrescriptionFailed,
  isPassportByCheckPrescriptionOldRules,
}: IErknmActViolationsProps & IErknmActViolationsStoreProps) => {
  const remove = formRef?.current.mutators.remove;
  const removeBatch = formRef?.current.mutators.removeBatch;

  const violatedRequirementsNpaFieldName = `${name}.violatedMandatoryRequirements`;

  const removeViolatedRequirementsNpa = (values?: IKnmErknmFormViolatedRequirementsNpa[]) => (
    npaIndex: number,
    npaId?: number,
  ) => {
    if (npaIndex !== -1) {
      remove && remove(violatedRequirementsNpaFieldName, npaIndex);
    } else if (npaId) {
      const indexes: number[] = [];

      values?.forEach((item, ind) => {
        if (item.npa.id === npaId) {
          indexes.push(ind);
        }
      });

      removeBatch && removeBatch(violatedRequirementsNpaFieldName, indexes);
    }
  };

  const removeStructuralUnit = (values?: IKnmErknmFormViolatedRequirementsNpa[]) => (structuralUnitId: number) => {
    if (structuralUnitId) {
      const indexes: number[] = [];

      values?.forEach((item, ind) => {
        if (item.structuralUnit.id === structuralUnitId) {
          indexes.push(ind);
        }
      });

      removeBatch && removeBatch(violatedRequirementsNpaFieldName, indexes);
    }
  };

  const handleIsViolatedMandatoryRequirementsChange = useCallback(
    async (v: IBooleanSelectOption) => {
      if (!v.value) {
        // @ts-ignore
        formRef?.current.change(violatedRequirementsNpaFieldName as keyof IKnmForm);
      } else if (
        /**
         *  Если:
         * 1) повторная проверка включена
         * 2) основание в паспорте "Повторная проверка" или "Невозможность проведения предыдущей проверки" по паспорту с основанием "Повторная проверка"
         *
         * то предзаполняем нарушения в акте
         */
        isRepeatCheckActive
      ) {
        let violatedMandatoryRequirements: IKnmErknmFormViolatedRequirementsNpa[] = [];

        if (isDecision) {
          // @ts-ignore
          const reasons = formRef?.current.getFieldState('reasons')?.value as IDecisionFormReason[] | undefined;

          const requirements = await getViolationsByReasonLink(
            isEnablePrescriptionCheck,
            isPrescriptionCheck || isPassportByCheckPrescriptionFailed === true,
            reasons?.[0]?.links?.[0],
          );

          violatedMandatoryRequirements = convertViolationDataToKnmErknmFormViolatedRequirementsNpa(requirements) || [];
        } else {
          // @ts-ignore
          const requirementsNpa = formRef?.current.getFieldState('requirementsNpaErknm')?.value as
            | IKnmErknmFormRequirementNpa[]
            | undefined;

          if (requirementsNpa) {
            // В акт импортируем только нарушения из предмета КНМ в статусе "Не устранено",
            // которые включены в предмет проверки (includeInSubject)
            violatedMandatoryRequirements = requirementsNpa
              .map(
                requirement =>
                  ({
                    ...requirement,
                    violatedMandatory: requirement.mandatory
                      .map(mandatory => ({
                        ...mandatory,
                        violations: mandatory.violations
                          ?.filter(viol => viol.status === ViolationStatusesEnum.NOT_FIXED && viol.includeInSubject)
                          .map(viol => ({
                            ...viol,
                            isNew: true,
                            repeatCorrectedInEvent: booleanSelectOptions[1],
                          })),
                      }))
                      .filter(mandatory => mandatory.violations?.length),
                  } as IKnmErknmFormViolatedRequirementsNpa),
              )
              .filter(requirement => requirement.violatedMandatory.length);
          }
        }

        // @ts-ignore
        formRef?.current.change(
          `${name}.violatedMandatoryRequirements` as keyof IKnmForm,
          violatedMandatoryRequirements,
        );
      }
    },
    [formRef, knmActTypesOptions],
  );

  const { events } = value;

  const isRepeatCheckActive =
    isEnablePrescriptionCheck &&
    (isPrescriptionCheck || isPassportByCheckPrescriptionFailed === true) &&
    isPassportByCheckPrescriptionOldRules === false;

  const isAllActionsNotPerformed: boolean = useMemo(() => {
    if (!events?.length) {
      return false;
    }

    return events.every(e => e?.actionNotPerformed);
  }, [events]);

  const isDisabledByRepeatCheck = isRepeatCheckActive;

  useEffect(() => {
    if (!isDisabledByRepeatCheck && !isDecision) {
      // Если в перечне осуществленных действий отсутсвуют элементы с проводимыми действиями, то раздел
      // "Выявленные нарушения обязательных требований" - программно очищаем и блокируем
      if (isAllActionsNotPerformed && value.isViolatedMandatoryRequirements?.value !== false) {
        // @ts-ignore
        formRef?.current.change(`${name}.isViolatedMandatoryRequirements`, booleanSelectOptions[1]);
        // @ts-ignore
        formRef?.current.change(violatedRequirementsNpaFieldName as keyof IKnmForm);
      } else if (!value.violatedMandatoryRequirements?.length) {
        // @ts-ignore
        formRef?.current.change(`${name}.isViolatedMandatoryRequirements`);
      }
    }
  }, [
    isDisabledByRepeatCheck,
    isDecision,
    isAllActionsNotPerformed,
    name,
    violatedRequirementsNpaFieldName,
    value.violatedMandatoryRequirements?.length,
  ]);

  return (
    <div className={styles.GroupMarginBottom}>
      <FieldBlock
        id={`${id}.isViolatedMandatoryRequirements`}
        label={getTitleFieldWithRedStar({
          label: 'Выявленные нарушения обязательных требований',
          field: `${name}.isViolatedMandatoryRequirements`,
          isDecision,
          isKnm: !isDecision,
          sectionName: isDecision ? `${name}.isViolatedMandatoryRequirements` : undefined,
        })}
        size="half"
      >
        <Row>
          <SelectInputField
            className={`shared-row-flex ${styles.SelectInputField}`}
            name={`${name}.isViolatedMandatoryRequirements`}
            options={booleanSelectOptions}
            isClearable={false}
            disabled={isDisabledField || (!isDecision && isAllActionsNotPerformed) || isDisabledByDirty}
            onChange={handleIsViolatedMandatoryRequirementsChange}
            size="medium"
          />
          {value.isViolatedMandatoryRequirements?.value === true && !isDisabledByRepeatCheck && (
            <Button
              className="shared-btn-add"
              text={getTitleFieldWithRedStar({
                label: 'Добавить',
                field: violatedRequirementsNpaFieldName,
                isDecision,
                isKnm: !isDecision,
              })}
              type="button"
              variant="link"
              color="primary"
              disabled={isDisabledField || isDisabledByDirty}
              onClick={() => addRequirementsNpa(name, violatedRequirementsNpaFieldName)}
            />
          )}
        </Row>
      </FieldBlock>
      <FormSpy subscription={{ errors: true, touched: true }}>
        {props => {
          let fieldError: string | undefined;

          if (isDecision) {
            fieldError = getFieldArrayErrorMessage(props.errors?.organization?.act?.violatedMandatoryRequirements);
          } else {
            fieldError =
              index !== undefined
                ? getFieldArrayErrorMessage(props.errors?.acts?.[index]?.violatedMandatoryRequirements)
                : undefined;
          }

          if (fieldError) {
            return <p className="shared-input-error shared-row__small-margin">{fieldError}</p>;
          }

          return null;
        }}
      </FormSpy>
      <FieldArray name={violatedRequirementsNpaFieldName}>
        {({ fields }) => {
          const byNpaId = groupBy<IKnmErknmFormViolatedRequirementsNpa>(item => item.npa.id.toString());
          const groupedSu = fields.value ? byNpaId(fields.value) : {};

          if (isPaperless) {
            const groupedKeys = Object.keys(groupedSu);
            const { visibleItems, hasMore, showMore, totalCount, visibleCount, isRendering } = useShowMore(
              groupedKeys,
              {
                initialCount: 10,
                incrementCount: 10,
              },
            );

            return (
              <>
                {visibleItems.map(key => {
                  const item = groupedSu[key];

                  return (
                    <GroupedViolatedRequirementsNpaItem
                      key={key}
                      groupNpa={item}
                      allItems={fields.value as IKnmErknmFormViolatedRequirementsNpa[]}
                      name={fields.name}
                      watchOnly={isDisabledField}
                      isViolationFieldsWatchOnly={isWatchOnly}
                      isDisabledByDirty={isDisabledByDirty}
                      onRemove={removeViolatedRequirementsNpa(fields.value as IKnmErknmFormViolatedRequirementsNpa[])}
                      onRemoveStructuralUnit={removeStructuralUnit(
                        fields.value as IKnmErknmFormViolatedRequirementsNpa[],
                      )}
                      addMandatory={(v, f) => addViolatedMandatory(v, f, `${name}.violatedMandatoryRequirements`)}
                      formRef={formRef}
                      isMandatoryDisabled={false}
                      id={id}
                      inAct
                      isPaperless={isPaperless}
                      isKnm={!isDecision}
                      isDecision={isDecision}
                      isDisabledByRepeatCheck={isDisabledByRepeatCheck}
                    />
                  );
                })}
                {isRendering && <Spinner className={styles.Loader} />}
                {hasMore && !isRendering && (
                  <ShowMoreButton onClick={showMore} totalCount={totalCount} visibleCount={visibleCount} />
                )}
              </>
            );
          }

          const { visibleItems: visibleNpas, hasMore, showMore, totalCount, visibleCount, isRendering } = useShowMore(
            fields.value,
            {
              initialCount: 10,
              incrementCount: 10,
            },
          );

          return (
            <>
              {visibleNpas.map((fieldValue: IKnmErknmFormViolatedRequirementsNpa) => {
                const npaIndex =
                  fields.value?.findIndex((item: IKnmErknmFormViolatedRequirementsNpa) => item === fieldValue) ?? 0;
                const npaName = `${violatedRequirementsNpaFieldName}[${npaIndex}]`;

                return (
                  <Field key={npaName} name={npaName}>
                    {({ meta }) => {
                      const isFieldTouched = meta.touched;
                      const errorField = meta.error;
                      const filedId = `${id}.violatedMandatoryRequirements[${
                        fieldValue.guid ? fieldValue.guid : npaIndex
                      }]`;

                      return (
                        <KnmViolatedRequirementsNpaItem
                          value={fieldValue}
                          name={npaName}
                          watchOnly={isDisabledField}
                          isViolationFieldsWatchOnly={isWatchOnly}
                          onRemove={() => removeViolatedRequirementsNpa()(npaIndex)}
                          addMandatory={addViolatedMandatory}
                          touched={isFieldTouched}
                          error={errorField}
                          formRef={formRef}
                          isMandatoryDisabled={false}
                          id={filedId}
                          inAct
                          isPaperless={isPaperless}
                          isKnm={!isDecision}
                          isDecision={isDecision}
                        />
                      );
                    }}
                  </Field>
                );
              })}
              {isRendering && <Spinner className={styles.Loader} />}
              {hasMore && !isRendering && (
                <ShowMoreButton onClick={showMore} totalCount={totalCount} visibleCount={visibleCount} />
              )}
            </>
          );
        }}
      </FieldArray>
    </div>
  );
};

// Компонент общий для КНМ и ПР, признаки повторной проверки живут в разных ветках
// стора: сага паспорта решения пишет их в decision, сага КНМ в knmErknm.
const mapStateToProps = (state: IRootState, ownProps: IErknmActViolationsProps): IErknmActViolationsStoreProps => ({
  knmActTypesOptions: getCatalogsKnmActTypesSelectOptions(state),
  isPassportByCheckPrescriptionFailed: ownProps.isDecision
    ? getDecisionIsPrescriptionFailed(state)
    : getKnmErknmIsPrescriptionFailed(state),
  isPassportByCheckPrescriptionOldRules: ownProps.isDecision
    ? getDecisionIsPrescriptionOldRules(state)
    : getKnmErknmIsPrescriptionOldRules(state),
});

export const ErknmActViolations = connect(mapStateToProps)(ErknmActViolationsWithoutStore);
